import { interpolateHsl as d3InterpolateHsl } from 'd3-interpolate'
import dayjs from 'dayjs'
import { calculateLPO } from 'features/fde'
import {
  AggregateHealthPlanComponentData,
  AhpPlan,
  GeckoAssetModel,
  GeckoEntryBase,
  GeckoUnit,
  HealthPlanAction,
} from 'types'

export interface PlanPlotData {
  planName: string
  unitName?: string
  actionCount?: number
  preferred: boolean
  planId: string
  planCost: number
  planActions: HealthPlanAction[]
  planRaw: GeckoEntryBase<AhpPlan>
}

export interface HealthPlanDataForPlot {
  unitName: string
  components: Map<string, AggregateHealthPlanComponentData>
  criticality: string
  unitRaw?: GeckoUnit
  plans: PlanPlotData[]
  assetModel: GeckoAssetModel | undefined
  area: string
  preferredNextActionDate?: string
  preferredPlanCost?: number
  lastInspectionDate: string
}

const minColor = 'hsl(60, 65%, 70%)'
const maxColor = 'hsl(60, 100%, 100%)'

export interface ChartDataConfig {
  aggregatedData: HealthPlanDataForPlot[]
  onlyPreferred?: boolean
  groupKey?: 'label' | 'planName'
  stacked?: boolean
  allYears?: string[]
}

export type ProcessedChartData = Array<{
  label: string
  data: Array<{ x: string; y: number }>
  backgroundColor: string
  borderColor: string
  borderWidth: number
  stack?: string
}>

export const createCostDatasets = (
  config: ChartDataConfig
): ProcessedChartData => {
  const {
    aggregatedData,
    onlyPreferred = true,
    groupKey = 'label',
    stacked = false,
    allYears = [],
  } = config

  // Process action costs
  const actionCosts = aggregatedData.flatMap((unit) =>
    unit.plans
      .filter((plan) => !onlyPreferred || plan.preferred)
      .flatMap((plan) =>
        plan.planActions.map((entry) => ({
          cost: entry.actionData.costTotal ?? 0,
          date: dayjs(entry.action.data.actionDate).format('YYYY'),
          label: unit.unitName,
          planName: plan.planName,
        }))
      )
  )

  const colorInterpolater = d3InterpolateHsl(minColor, maxColor)

  const uniqueLabelsByKey = [
    ...new Set(actionCosts.map((p) => p[groupKey])),
  ].sort()

  // Create datasets
  const totalUniqueLables = uniqueLabelsByKey.length
  const datasets = uniqueLabelsByKey.map((label, i) => {
    const data = allYears.map((year) => {
      const totalCostForYear = actionCosts
        .filter((p) => p[groupKey] === label && p.date === year)
        .reduce((sum, p) => sum + p.cost, 0)
      return {
        x: year,
        y: totalCostForYear,
      }
    })

    return {
      label,
      data,
      backgroundColor: colorInterpolater(i / totalUniqueLables),
      borderColor: colorInterpolater(i / totalUniqueLables),
      borderWidth: 1,
      stacked: stacked ? undefined : label,
    }
  })

  return datasets
}

export const createLPODataSets = (
  config: ChartDataConfig
): ProcessedChartData => {
  const { aggregatedData, stacked = true, allYears = [] } = config
  // Group units by area
  const unitsByArea = aggregatedData.reduce<
    Record<string, HealthPlanDataForPlot[]>
  >((acc, unit) => {
    if (!unit.area) return acc
    if (!acc[unit.area]) {
      acc[unit.area] = []
    }
    acc[unit.area].push(unit)
    return acc
  }, {})

  // Calculate min health score for each unit within its area for each year
  const minHealthScoreByArea = Object.fromEntries(
    Object.entries(unitsByArea).map(([area, units]) => {
      const yearlyMinScores = allYears.map((year) => {
        const scoresWithUnits = units.flatMap((unit) =>
          Array.from(unit.components.values()).map((component) => ({
            score:
              component.preferredPlanDamageAreas?.[year]?.healthScore ?? 100,
            unit: unit,
            componentName: component.componentName,
          }))
        )

        const minScore = Math.min(...scoresWithUnits.map((s) => s.score))
        const minScoreEntry = scoresWithUnits.find((s) => s.score === minScore)

        return [
          year,
          {
            score: minScore,
            unit: minScoreEntry?.unit,
          },
        ] as const
      })
      return [area, Object.fromEntries(yearlyMinScores)] as const
    })
  )

  const colorInterpolater = d3InterpolateHsl(minColor, maxColor)
  const totalAreaLength = Object.keys(minHealthScoreByArea).length

  return Object.entries(minHealthScoreByArea).map(
    ([area, yearlyMinScores], i) => {
      const data = Object.entries(yearlyMinScores).map(
        ([year, minScoreEntry]) => {
          if (!minScoreEntry.unit) {
            return {
              x: year,
              y: 0,
            }
          }

          const lpoCost =
            calculateLPO({
              assetComponents: minScoreEntry.unit.components,
              year: dayjs(year),
              criticality: parseInt(minScoreEntry.unit.criticality),
              unit: minScoreEntry.unit.unitRaw,
              minimumHealthScore: minScoreEntry.score,
            }) ?? 0

          return {
            x: year,
            y: lpoCost,
          }
        }
      )

      return {
        label: `${area} LPO`,
        data,
        backgroundColor: colorInterpolater(i / totalAreaLength),
        borderColor: colorInterpolater(i / totalAreaLength),
        borderWidth: 1,
        stacked: stacked ? undefined : area,
      }
    }
  )
}

export const createHealthPlanPlotData = (
  aggregateData: HealthPlanDataForPlot[],
  yearsToDisplay: number[] = [],
  opts?: {
    onlyPreferred?: boolean
    groupKey?: 'label' | 'planName'
    stacked?: boolean
  }
) => {
  const allYears = yearsToDisplay.map((year) => year.toString())

  const costDataSet = createCostDatasets({
    aggregatedData: aggregateData,
    onlyPreferred: true,
    groupKey: 'label',
    stacked: true,
    allYears,
    ...opts,
  })

  const lpoGraphData = createLPODataSets({
    aggregatedData: aggregateData,
    onlyPreferred: true,
    groupKey: 'label',
    stacked: true,
    allYears,
    ...opts,
  })

  return {
    labels: allYears,
    datasets: [...costDataSet, ...lpoGraphData],
  }
}
